import { useMemo, useState, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Table2, BarChart3 } from 'lucide-react';
import { useUi } from '@/store/ui';
import { Card, CardBody, CardHeader, EmptyState } from './ui';

/** Resolve theme CSS variables to concrete colours for SVG (re-read when the theme toggles). */
export function useChartColors() {
  const theme = useUi((s) => s.theme);
  return useMemo(() => {
    const cs = getComputedStyle(document.documentElement);
    const v = (n: string) => cs.getPropertyValue(n).trim();
    return { s1: v('--series-1'), s2: v('--series-2'), grid: v('--chart-grid'), ink: v('--ink'), muted: v('--ink-subtle'), surface: v('--surface'), line: v('--line') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
}

export interface Series {
  key: string;
  label: string;
}

function TooltipBox({ active, payload, label, labelFormatter, valueFormatter }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string; labelFormatter?: (l: string) => string; valueFormatter?: (n: number) => string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-lg">
      <p className="mb-1 font-medium text-ink">{labelFormatter ? labelFormatter(String(label)) : label}</p>
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-2 text-ink-muted">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} aria-hidden />
          {p.name}: <span className="tabular font-medium text-ink">{valueFormatter ? valueFormatter(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

function Legend({ series, colors }: { series: Series[]; colors: string[] }) {
  if (series.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-4 text-xs text-ink-muted">
      {series.map((s, i) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colors[i] }} aria-hidden />
          {s.label}
        </span>
      ))}
    </div>
  );
}

/** Card wrapper with a chart ⇄ table toggle so the data is never colour-only. */
export function ChartCard<T extends Record<string, unknown>>({
  title,
  subtitle,
  data,
  xKey,
  series,
  xFormat,
  kind = 'bar',
  height = 240,
  emptyText = 'No data for this period yet.',
  action,
  yFormat,
  valueFormat,
}: {
  title: string;
  subtitle?: string;
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  xFormat?: (v: string) => string;
  kind?: 'bar' | 'line';
  height?: number;
  emptyText?: string;
  action?: ReactNode;
  /** Short axis labels for large numbers (e.g. compact currency). */
  yFormat?: (n: number) => string;
  /** Full value format for the tooltip and table view. */
  valueFormat?: (n: number) => string;
}) {
  const c = useChartColors();
  const colors = [c.s1, c.s2];
  const [asTable, setAsTable] = useState(false);
  const empty = data.every((d) => series.every((s) => !Number(d[s.key])));
  const tick = { fill: c.muted, fontSize: 12 };

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex items-center gap-2">
            {action}
            <button type="button" onClick={() => setAsTable((t) => !t)} className="rounded-md p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-ink" aria-label={asTable ? 'Show chart' : 'Show as table'} title={asTable ? 'Show chart' : 'Show as table'}>
              {asTable ? <BarChart3 className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
            </button>
          </div>
        }
      />
      <CardBody>
        <div className="mb-3"><Legend series={series} colors={colors} /></div>
        {empty ? (
          <EmptyState title={emptyText} className="py-8" />
        ) : asTable ? (
          <div className="max-h-[260px] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-ink-subtle">
                  <th className="py-1.5 font-medium">{xKey}</th>
                  {series.map((s) => <th key={s.key} className="py-1.5 text-right font-medium">{s.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.map((d) => (
                  <tr key={String(d[xKey])} className="border-b border-line last:border-0">
                    <td className="py-1.5 text-ink-muted">{xFormat ? xFormat(String(d[xKey])) : String(d[xKey])}</td>
                    {series.map((s) => <td key={s.key} className="tabular py-1.5 text-right text-ink">{valueFormat ? valueFormat(Number(d[s.key] ?? 0)) : String(d[s.key] ?? 0)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ height }} role="img" aria-label={`${title}: ${series.map((s) => s.label).join(' and ')} by ${xKey}`}>
            <ResponsiveContainer width="100%" height="100%">
              {kind === 'bar' ? (
                <BarChart data={data} barGap={2} barCategoryGap="28%" margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={c.grid} />
                  <XAxis dataKey={xKey as never} tickFormatter={xFormat} tick={tick} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={tick} axisLine={false} tickLine={false} width={yFormat ? 64 : 44} tickFormatter={yFormat} />
                  <Tooltip cursor={{ fill: c.grid }} content={<TooltipBox labelFormatter={xFormat} valueFormatter={valueFormat} />} />
                  {series.map((s, i) => (
                    <Bar key={s.key} dataKey={s.key} name={s.label} fill={colors[i]} radius={[4, 4, 0, 0]} maxBarSize={14} />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={c.grid} />
                  <XAxis dataKey={xKey as never} tickFormatter={xFormat} tick={tick} axisLine={false} tickLine={false} minTickGap={16} />
                  <YAxis allowDecimals tick={tick} axisLine={false} tickLine={false} width={yFormat ? 64 : 44} tickFormatter={yFormat} />
                  <Tooltip cursor={{ stroke: c.muted, strokeDasharray: '3 3' }} content={<TooltipBox labelFormatter={xFormat} valueFormatter={valueFormat} />} />
                  {series.map((s, i) => (
                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={colors[i]} strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: c.surface, strokeWidth: 2 }} />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Ranked horizontal bars rendered in HTML (one series → one colour; labels stay in ink). */
export function RankedBars({ items, valueFormat }: { items: { label: string; value: number; sub?: string }[]; valueFormat?: (n: number) => string }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length || items.every((i) => !i.value)) return <EmptyState title="No data for this period yet." className="py-8" />;
  return (
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it.label} title={`${it.label}: ${valueFormat ? valueFormat(it.value) : it.value}`}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-ink">{it.label}</span>
            <span className="tabular shrink-0 text-ink-muted">
              {valueFormat ? valueFormat(it.value) : it.value}
              {it.sub && <span className="ml-1 text-xs text-ink-subtle">{it.sub}</span>}
            </span>
          </div>
          <div className="h-2 rounded-full bg-surface-hover">
            <div className="h-2 rounded-full bg-[var(--series-1)]" style={{ width: `${(it.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
